import { API_BASE_URL } from './api';

function authHeaders(): Record<string, string> {
  let token: string | null = null;
  try {
    token = localStorage.getItem('auth_token');
  } catch {
    token = null;
  }
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export type GemmaQueuePdfFileDto = {
  name: string;
  driveWebViewLink?: string | null;
};

export type GemmaQueueStandaloneOfferDto = {
  id: string;
  offerCode: string | null;
  orderName: string | null;
  /** Miejscowość / lokalizacja z karty oferty (jak w formularzu OF). */
  offerLocation?: string | null;
};

export type GemmaQueueJobDto = {
  id: string;
  /** Właściciel zadania (user_id) — do ACL po stronie UI; admin może zarządzać cudzymi. */
  ownerUserId?: string;
  estimateId: number;
  title: string;
  /** Z JOIN estimate — do podglądu / kontekstu. */
  estimateName?: string | null;
  estimateBranza?: string | null;
  estimateNumber?: string | null;
  estimateObjectName?: string | null;
  estimateLocation?: string | null;
  standaloneOffer?: GemmaQueueStandaloneOfferDto | null;
  /** PDF-y przedmiaru — nazwa + link do kopii na Google Drive (folder oferty). */
  pdfFiles?: GemmaQueuePdfFileDto[];
  requester: { displayName: string; avatarUrl?: string | null };
  status: 'queued' | 'paused' | 'running' | 'done' | 'failed';
  scheduledAt: string | null;
  scheduleAfterLp: number | null;
  canEditSchedule: boolean;
  errorMessage: string | null;
  createdAt: string | null;
};

export async function fetchGemmaQueueJobs(): Promise<GemmaQueueJobDto[]> {
  const res = await fetch(`${API_BASE_URL}/gemma-queue/jobs`, {
    headers: { ...authHeaders() },
    credentials: 'include',
  });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j?.message || `HTTP ${res.status}`);
  if (!j?.success) throw new Error(j?.message || 'Błąd listy kolejki');
  return Array.isArray(j.jobs) ? j.jobs : [];
}

export async function enqueuePrzedmiarPdf(
  estimateId: number,
  files: File[],
  opts?: { extraPrompt?: string | null }
): Promise<{ message: string; jobId: string; scheduledAt: string | null }> {
  const form = new FormData();
  for (const f of files) {
    form.append('files', f);
  }
  form.append('estimateId', String(estimateId));
  const extraPrompt = opts?.extraPrompt != null ? String(opts.extraPrompt).trim() : '';
  if (extraPrompt.length > 0) {
    form.append('extraPrompt', extraPrompt);
  }
  const res = await fetch(`${API_BASE_URL}/gemma-queue/jobs/przedmiar-pdf`, {
    method: 'POST',
    headers: { ...authHeaders() },
    body: form,
    credentials: 'include',
  });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j?.message || `HTTP ${res.status}`);
  if (!j?.success) throw new Error(j?.message || 'Nie udało się dodać zadania');
  return {
    message: String(j.message || 'Dodano do kolejki.'),
    jobId: String(j.jobId || ''),
    scheduledAt: j.scheduledAt != null ? String(j.scheduledAt) : null,
  };
}

export async function patchGemmaQueueSchedule(jobId: string, scheduledAtIso: string): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/gemma-queue/jobs/${encodeURIComponent(jobId)}/schedule`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ scheduledAt: scheduledAtIso }),
    credentials: 'include',
  });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j?.message || `HTTP ${res.status}`);
  if (!j?.success) throw new Error(j?.message || 'Błąd zapisu terminu');
}

export async function deleteGemmaQueueJob(jobId: string): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/gemma-queue/jobs/${encodeURIComponent(jobId)}`, {
    method: 'DELETE',
    headers: { ...authHeaders() },
    credentials: 'include',
  });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j?.message || `HTTP ${res.status}`);
  if (!j?.success) throw new Error(j?.message || 'Błąd usuwania');
}

export async function playGemmaQueueJob(jobId: string): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/gemma-queue/jobs/${encodeURIComponent(jobId)}/play`, {
    method: 'POST',
    headers: { ...authHeaders() },
    credentials: 'include',
  });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j?.message || `HTTP ${res.status}`);
  if (!j?.success) throw new Error(j?.message || 'Nie można uruchomić');
}

export async function pauseGemmaQueueJob(jobId: string): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/gemma-queue/jobs/${encodeURIComponent(jobId)}/pause`, {
    method: 'POST',
    headers: { ...authHeaders() },
    credentials: 'include',
  });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j?.message || `HTTP ${res.status}`);
  if (!j?.success) throw new Error(j?.message || 'Błąd pauzy');
}

export async function completeGemmaQueueJobClient(
  jobId: string,
  opts?: { lastGemmaOutput?: string | null }
): Promise<void> {
  const body: Record<string, unknown> = {};
  if (opts?.lastGemmaOutput != null && opts.lastGemmaOutput !== '') {
    body.lastGemmaOutput = opts.lastGemmaOutput;
  }
  const res = await fetch(`${API_BASE_URL}/gemma-queue/jobs/${encodeURIComponent(jobId)}/complete-client`, {
    method: 'POST',
    headers: {
      ...(Object.keys(body).length ? { 'Content-Type': 'application/json' } : {}),
      ...authHeaders(),
    },
    body: Object.keys(body).length ? JSON.stringify(body) : undefined,
    credentials: 'include',
  });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j?.message || `HTTP ${res.status}`);
  if (!j?.success) throw new Error(j?.message || 'Błąd zakończenia zadania');
}

export async function failGemmaQueueJobClient(
  jobId: string,
  message: string,
  opts?: { lastGemmaOutput?: string | null }
): Promise<void> {
  const payload: Record<string, unknown> = { message };
  if (opts?.lastGemmaOutput != null && opts.lastGemmaOutput !== '') {
    payload.lastGemmaOutput = opts.lastGemmaOutput;
  }
  const res = await fetch(`${API_BASE_URL}/gemma-queue/jobs/${encodeURIComponent(jobId)}/fail-client`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(payload),
    credentials: 'include',
  });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j?.message || `HTTP ${res.status}`);
  if (!j?.success) throw new Error(j?.message || 'Błąd');
}

export type GemmaQueueJobPreviewDto = {
  status: GemmaQueueJobDto['status'];
  errorMessage: string | null;
  lastGemmaOutput: string | null;
  /** Długość bufora odpowiedzi (także w trakcie generowania). */
  outputCharCount: number;
  /** Kiedy ostatnio zaktualizowano rekord (np. zapis częściowy streamu). ISO. */
  progressUpdatedAt: string | null;
  /** Start zadania (running). ISO. */
  startedAt: string | null;
};

/** Uruchamia zadanie na serwerze, jeśli jest w kolejce / wstrzymane. */
export async function ensureGemmaPrzedmiarJobRunning(jobId: string): Promise<void> {
  const p = await fetchGemmaQueueJobPreview(jobId);
  if (p.status === 'queued' || p.status === 'paused') {
    await playGemmaQueueJob(jobId);
  }
}

/** Polling podglądu do statusu końcowego (wynik = `done` / `failed` w DB), bez utrzymania SSE. */
export async function pollGemmaPrzedmiarJobUntilTerminal(
  jobId: string,
  onTick: (p: GemmaQueueJobPreviewDto) => void,
  opts?: { intervalMs?: number; maxWaitMs?: number; signal?: AbortSignal }
): Promise<GemmaQueueJobPreviewDto> {
  const intervalMs = opts?.intervalMs ?? 1500;
  const maxWaitMs = opts?.maxWaitMs ?? 3 * 60 * 60 * 1000;
  const t0 = Date.now();
  for (;;) {
    if (opts?.signal?.aborted) throw new Error('Przerwano');
    if (Date.now() - t0 > maxWaitMs) {
      throw new Error('Przekroczono limit czasu oczekiwania na zakończenie zadania na serwerze (3 h).');
    }
    const p = await fetchGemmaQueueJobPreview(jobId);
    onTick(p);
    if (p.status === 'done' || p.status === 'failed') return p;
    await new Promise((r) => setTimeout(r, intervalMs));
  }
}

export async function fetchGemmaQueueJobPreview(jobId: string): Promise<GemmaQueueJobPreviewDto> {
  const res = await fetch(`${API_BASE_URL}/gemma-queue/jobs/${encodeURIComponent(jobId)}/preview`, {
    headers: { ...authHeaders() },
    credentials: 'include',
  });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j?.message || `HTTP ${res.status}`);
  if (!j?.success) throw new Error(j?.message || 'Błąd podglądu');
  return {
    status: j.status,
    errorMessage: j.errorMessage != null ? String(j.errorMessage) : null,
    lastGemmaOutput: j.lastGemmaOutput != null ? String(j.lastGemmaOutput) : null,
    outputCharCount: Number.isFinite(Number(j.outputCharCount)) ? Math.max(0, Math.floor(Number(j.outputCharCount))) : 0,
    progressUpdatedAt: j.progressUpdatedAt != null ? String(j.progressUpdatedAt) : null,
    startedAt: j.startedAt != null ? String(j.startedAt) : null,
  };
}

export async function fetchGemmaQueueJobFilesMeta(jobId: string): Promise<{ index: number; name: string }[]> {
  const res = await fetch(`${API_BASE_URL}/gemma-queue/jobs/${encodeURIComponent(jobId)}/files`, {
    headers: { ...authHeaders() },
    credentials: 'include',
  });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j?.message || `HTTP ${res.status}`);
  if (!j?.success) throw new Error(j?.message || 'Błąd metadanych plików');
  return Array.isArray(j.files) ? j.files : [];
}

/** Pobiera PDF-y z serwera jako `File[]` do `przedmiarPdfExtractStream`. */
export async function fetchGemmaQueueJobFilesAsFiles(jobId: string): Promise<File[]> {
  const meta = await fetchGemmaQueueJobFilesMeta(jobId);
  const out: File[] = [];
  for (let i = 0; i < meta.length; i++) {
    const r = await fetch(`${API_BASE_URL}/gemma-queue/jobs/${encodeURIComponent(jobId)}/files/${i}`, {
      headers: { ...authHeaders() },
      credentials: 'include',
    });
    if (!r.ok) throw new Error(`Błąd pobierania pliku ${i}`);
    const blob = await r.blob();
    const name = meta[i]?.name || `file-${i}.pdf`;
    out.push(new File([blob], name, { type: 'application/pdf' }));
  }
  return out;
}
